import { Flex } from '@chakra-ui/react';
import { DropdownPopover } from 'modules/common/components/ToolbarPopover';
import { generateTranslatedOptions } from 'modules/common/components/ToolbarPopover/DropdownPopover';
import { ReactElement, memo } from 'react';
import { useTranslation } from 'react-i18next';

import { BusinessUpgradeButton } from './BusinessUpgradeButton';
import { DROPDOWN_OPTIONS, FolderProps } from './Folders.helper';
import NewFolderButton from './NewFolderButton';

const defaultStyle = {
  defaultWH: '20rem',
  hideText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
};

interface FoldersListProps {
  value?: string | number;
  allowCreate?: boolean;
  onChange: (selectedFolder: string) => void;
  onClickNewFolder: () => void;
  usersFolders: FolderProps[];
  onClickUpgradeModal: () => void;
}
/**
 * Component with a dropdown to allow users select a folder
 */
const FoldersList = memo(
  ({
    value = 0,
    onChange,
    onClickNewFolder = () => {},
    usersFolders = [],
    allowCreate = true,
    onClickUpgradeModal,
  }: FoldersListProps): ReactElement => {
    const { t } = useTranslation('editor_widget_menu', { useSuspense: false });

    const foldersList = [...generateTranslatedOptions(t, DROPDOWN_OPTIONS), ...usersFolders];

    const selectedOptionIndex = !value
      ? 0
      : Object.values(foldersList).findIndex((option) => {
          return option.value === value;
        });

    const label = foldersList[selectedOptionIndex ?? 0]?.label;

    return (
      <>
        <DropdownPopover
          options={foldersList}
          selectedIndex={selectedOptionIndex ?? 0}
          label={label}
          w='100%'
          maxH={'24rem'}
          minW={defaultStyle.defaultWH}
          maxW={defaultStyle.defaultWH}
          spacing={1}
          buttonVariant={'toolbar-dropdown-option'}
          optionsButtonVariant={'toolbar-dropdown-item'}
          onSelect={onChange}
          triggerSelectOnSameValue={false}
          closeOnSelect={true}
          triggerProps={{
            size: 'sm',
            'data-testid': 'folders-list-trigger',
            minW: '200px',
            px: '2',
            border: '1px solid',
            borderColor: 'var(--vg-colors-gray-200)',
            borderRadius: 'base',
          }}
          buttonOptionProps={{
            height: 7,
            justifyContent: 'flex-start',
            ...defaultStyle.hideText,
            _active: {
              bg: 'hover.blue',
              '& path': {
                stroke: 'upgrade.blue.700',
              },
              color: 'upgrade.blue.700',
            },
          }}
          textButtonProps={{
            maxW: '180px',
            ...defaultStyle.hideText,
          }}
          optionProps={{
            ...defaultStyle.hideText,
          }}
          useLabelInsteadOfSelectedLabel
          isTooltipDisabled
        >
          <>
            <NewFolderButton
              isViewOnly={!allowCreate}
              onClick={onClickNewFolder}
              text={t('sideMenu.imagesMenu.folders.create')}
            />
            {!allowCreate && (
              <Flex justifyContent='center' pt='1'>
                <BusinessUpgradeButton onClickUpgradeModal={onClickUpgradeModal} />
              </Flex>
            )}
          </>
        </DropdownPopover>
      </>
    );
  },
);
FoldersList.displayName = 'FoldersList';

export default FoldersList;
